/**
 * Aritmetica de dinero y cantidades.
 *
 * INVARIANTE DEL PROYECTO
 * -----------------------
 * Dinero      -> enteros en CENTAVOS.   1550 === $15.50 MXN.   Sufijo `_cents`.
 * Cantidades  -> enteros en MILESIMAS.   750 === 0.750 kg.      Sufijo `_milli`.
 *
 * El punto flotante NO se usa para representar dinero en ningun punto del sistema:
 * ni en la base de datos, ni en la API, ni en el estado del cliente. Solo aparece
 * al final, cuando se formatea para el ojo humano.
 *
 * Motivo concreto: 0.1 + 0.2 === 0.30000000000000004. En una caja registradora eso
 * es un descuadre que alguien tiene que pagar de su bolsa al final del turno.
 *
 * Este modulo es puro y no tiene dependencias. Toda la aritmetica monetaria del
 * sistema pasa por aqui; no repliques estas formulas en otros archivos.
 */
/** Milesimas que forman una unidad completa (1 pieza, 1 kg, 1 litro). */
export const MILLI = 1000;
/**
 * Cantidad maxima de un renglon, en milesimas: un millon de unidades.
 *
 * Es el espejo exacto de la cota de la base (`chk_si_qty_max` y sus hermanas en
 * compras y traspasos). Vive aqui para que la captura pueda frenar mientras se
 * teclea, en vez de dejar que el cobro rebote con un error que no explica nada
 * con el cliente enfrente. La cota de verdad sigue siendo la de Postgres: esta
 * es cortesia, no control.
 */
export const MAX_LINE_QTY_MILLI = 1_000_000_000;
/**
 * Importe unitario maximo, en centavos: diez millones de pesos por unidad.
 *
 * El otro espejo de la misma familia de cotas (`chk_sp_cost_max`,
 * `chk_sp_price_max`, `chk_pi_cost_max`, `chk_si_price_max`). Vale para un
 * precio y para un costo porque en la base es el mismo numero.
 */
export const MAX_UNIT_AMOUNT_CENTS = 1_000_000_000;
/**
 * Cota superior de seguridad para los productos intermedios de la aritmetica.
 * 2^52 deja un factor 2 de margen antes de Number.MAX_SAFE_INTEGER (2^53 - 1),
 * que es donde los enteros de JavaScript empiezan a perder precision.
 */
export const SAFE_INT_LIMIT = 2 ** 52;
export class MoneyError extends Error {
    constructor(message) {
        super(message);
        this.name = 'MoneyError';
    }
}
/** Verifica que un valor sea un entero utilizable como centavos o milesimas. */
export function assertSafeInt(value, label = 'valor') {
    if (!Number.isInteger(value)) {
        throw new MoneyError(`${label} debe ser un entero, se recibio: ${value}`);
    }
    if (Math.abs(value) > SAFE_INT_LIMIT) {
        throw new MoneyError(`${label} excede el rango entero seguro: ${value}`);
    }
    return value;
}
/**
 * Division entera con redondeo "medio hacia arriba" (half-up), alejandose del cero.
 *
 * Se usa half-up y no redondeo bancario porque es lo que espera el comercio al
 * menudeo: medio centavo siempre sube. El redondeo bancario es correcto para
 * agregados contables, no para el importe que ve el cliente en el mostrador.
 *
 * Ejemplos: 1500/1000 -> 2 ;  1400/1000 -> 1 ;  -1500/1000 -> -2
 */
export function divRoundHalfUp(numerator, denominator) {
    assertSafeInt(numerator, 'numerador');
    assertSafeInt(denominator, 'denominador');
    if (denominator <= 0) {
        throw new MoneyError(`el denominador debe ser positivo, se recibio: ${denominator}`);
    }
    const sign = numerator < 0 ? -1 : 1;
    const abs = Math.abs(numerator);
    const scaled = abs * 2 + denominator;
    assertSafeInt(scaled, 'producto intermedio del redondeo');
    return sign * Math.floor(scaled / (2 * denominator));
}
/**
 * Importe de una linea de venta.
 *
 * Es la UNICA forma permitida de multiplicar precio por cantidad en el sistema.
 * Redondea exactamente una vez, al final, de forma que la suma de las lineas
 * siempre coincide con lo que el cliente ve impreso linea por linea.
 *
 * @param unitPriceCents precio por unidad completa (por pieza o por kilo)
 * @param qtyMilli       cantidad en milesimas (750 === 0.750 kg)
 */
export function lineTotalCents(unitPriceCents, qtyMilli) {
    assertSafeInt(unitPriceCents, 'precio unitario');
    assertSafeInt(qtyMilli, 'cantidad');
    if (unitPriceCents < 0)
        throw new MoneyError('el precio unitario no puede ser negativo');
    if (qtyMilli < 0)
        throw new MoneyError('la cantidad no puede ser negativa');
    const product = unitPriceCents * qtyMilli;
    assertSafeInt(product, 'importe de linea');
    return divRoundHalfUp(product, MILLI);
}
/**
 * Cuanta mercancia cabe en un importe. Es la INVERSA de `lineTotalCents`.
 *
 * POR QUE EXISTE
 * --------------
 * En el mostrador se pide de las dos maneras. "Medio kilo de jitomate" es una
 * cantidad, y para eso esta `lineTotalCents`. Pero "dame cinco pesos de chile"
 * es un IMPORTE, y ahi el numero que la persona trae decidido es el dinero: la
 * cantidad es la consecuencia. Sin esta funcion, quien cobra tiene que dividir
 * de cabeza cinco entre veintitres cincuenta con el cliente enfrente, y ese es
 * exactamente el momento en que se teclea de mas.
 *
 * NO REDONDEA LA CANTIDAD: BUSCA EL IMPORTE MAS CERCANO
 * -----------------------------------------------------
 * Lo obvio seria `round(importe x 1000 / precio)` y esta mal por una vuelta de
 * mas: redondear la cantidad y volver a redondear al multiplicar deja el cobro
 * mas lejos del importe pedido de lo necesario. Con chile a $23.50 el kilo y
 * cinco pesos: la cantidad "redonda" son 213 milesimas, que cobradas dan $5.01,
 * cuando 212 dan $4.98 — la primera es la buena, pero eso hay que COMPROBARLO,
 * no suponerlo. Asi que se evaluan las dos candidatas con `lineTotalCents` —la
 * unica funcion que puede decir cuanto se va a cobrar de verdad— y gana la que
 * quede mas cerca del importe pedido.
 *
 * Y solo hay dos candidatas: `lineTotalCents` no decrece cuando la cantidad
 * sube, asi que la mejor cantidad por abajo es el cociente entero y la mejor
 * por arriba es la siguiente. Ninguna otra puede ganarles.
 *
 * EL EMPATE SE VA HACIA ABAJO. Con un precio de $20.00 el kilo, $5.01 queda a
 * un centavo de $5.00 y a un centavo de $5.02: las dos son igual de exactas y
 * se cobra la de abajo, porque quien pidio cinco pesos trae cinco pesos en la
 * mano y cobrarle de mas necesita una explicacion que cobrarle de menos no.
 *
 * DAR EXACTO NO SIEMPRE SE PUEDE, Y NO ES UN DEFECTO. La cantidad se guarda en
 * milesimas, asi que un gramo de jamon de $150 el kilo vale 15 centavos: entre
 * un gramo y el siguiente no hay forma de aterrizar en $10.00 clavados. Quien
 * llama tiene que ENSEÑAR lo que se va a cobrar —volviendo a pasar el resultado
 * por `lineTotalCents`— en vez de repetir el importe que le pidieron. Es la
 * misma regla de la entrada de mercancia: si $456 entre 72 piezas dejan $455.76,
 * se dice.
 *
 * Esta funcion NO tiene gemela en SQL, a diferencia de `lineTotalCents` y de
 * `weightedAvgCostCents`, y no le falta: convertir dinero a cantidad es una
 * ayuda para CAPTURAR. Lo que viaja a la base sigue siendo una cantidad en
 * milesimas y el importe lo sigue calculando `private.line_total_cents`.
 *
 * @param amountCents     lo que el cliente pidio, en centavos
 * @param unitPriceCents  precio por unidad completa (por kilo, por litro)
 * @returns la cantidad en milesimas, o `null` si no hay precio con el cual
 *          dividir. Sin precio no se adivina: no hay cantidad que derivar.
 */
export function qtyMilliFromMoneyCents(amountCents, unitPriceCents) {
    assertSafeInt(amountCents, 'importe');
    assertSafeInt(unitPriceCents, 'precio unitario');
    if (amountCents < 0)
        throw new MoneyError('el importe no puede ser negativo');
    if (unitPriceCents <= 0)
        return null;
    const scaled = amountCents * MILLI;
    assertSafeInt(scaled, 'importe escalado a milesimas');
    // Cociente entero EXACTO. `Math.floor(a / b)` no basta: la division flotante
    // puede devolver 212.99999999999997 donde el cociente real es 213 y el piso
    // se cae un entero. La correccion es por multiplicacion, que dentro del
    // entero seguro si es exacta.
    let lower = Math.floor(scaled / unitPriceCents);
    if (lower * unitPriceCents > scaled)
        lower -= 1;
    else if ((lower + 1) * unitPriceCents <= scaled)
        lower += 1;
    const upper = lower + 1;
    const lowerGap = Math.abs(lineTotalCents(unitPriceCents, lower) - amountCents);
    const upperGap = Math.abs(lineTotalCents(unitPriceCents, upper) - amountCents);
    return upperGap < lowerGap ? upper : lower;
}
/**
 * Costo promedio ponderado despues de una entrada de mercancia.
 *
 * Es la OTRA formula que existe dos veces —una por lenguaje— y tiene que dar el
 * mismo entero en las dos:
 *
 *   TypeScript: esta funcion, para la vista previa mientras se captura.
 *   SQL:        `private.weighted_avg_cost()`, que es la que de verdad escribe.
 *
 * La copia de aqui NO decide nada: solo enseña, antes de guardar, en cuanto va a
 * quedar el costo y por lo tanto el margen. Si divergieran, la pantalla estaria
 * prometiendo un margen que la base no va a escribir.
 *
 * POR QUE PROMEDIO Y NO "EL ULTIMO COSTO"
 * Quedan 8 kg comprados a $120 y llegan 25 a $137: el costal viejo no se
 * evapora, y valuar todo a $137 infla el valor del inventario y hunde el margen
 * de lo que ya estaba. El promedio ponderado reparte cada peso donde se pago.
 *
 * Sin existencia previa no hay nada que promediar. Con existencia NEGATIVA
 * tampoco: es un descuadre que el sistema permite a proposito (§10) y
 * promediarla contra el costo nuevo daria un costo inventado.
 *
 * @param curStockMilli existencia antes de la entrada; puede ser negativa
 * @param curCostCents  costo promedio actual, por unidad completa
 * @param inQtyMilli    cantidad que entra, en milesimas
 * @param inCostCents   costo de lo que entra, por unidad completa
 */
export function weightedAvgCostCents(curStockMilli, curCostCents, inQtyMilli, inCostCents) {
    assertSafeInt(curStockMilli, 'existencia actual');
    assertSafeInt(curCostCents, 'costo actual');
    assertSafeInt(inQtyMilli, 'cantidad que entra');
    assertSafeInt(inCostCents, 'costo de la entrada');
    if (curCostCents < 0)
        throw new MoneyError('el costo actual no puede ser negativo');
    if (inCostCents < 0)
        throw new MoneyError('el costo de la entrada no puede ser negativo');
    if (inQtyMilli < 0)
        throw new MoneyError('la cantidad que entra no puede ser negativa');
    if (curStockMilli <= 0)
        return inCostCents;
    const total = curStockMilli + inQtyMilli;
    if (total <= 0)
        return inCostCents;
    // La version SQL calcula sobre `numeric` de precision arbitraria y no puede
    // desbordar jamas. Esta corre sobre enteros de JavaScript, asi que el producto
    // intermedio se verifica: mas vale que falle ruidosamente a que devuelva un
    // costo falso con el que alguien fije un precio.
    const weighted = curStockMilli * curCostCents + inQtyMilli * inCostCents;
    assertSafeInt(weighted, 'valor ponderado del inventario');
    return divRoundHalfUp(weighted, total);
}
/** Suma segura de centavos. Falla ruidosamente si algo no es entero. */
export function sumCents(values) {
    let total = 0;
    for (const v of values) {
        total += assertSafeInt(v, 'importe');
    }
    return assertSafeInt(total, 'suma de importes');
}
/** Utilidad bruta de una linea: precio menos costo. Puede ser negativa. */
export function marginCents(priceCents, costCents) {
    return assertSafeInt(priceCents, 'precio') - assertSafeInt(costCents, 'costo');
}
/**
 * Margen sobre el precio de venta, en porcentaje.
 * Devuelve null si el precio es cero (el porcentaje seria indefinido, no infinito).
 */
export function marginPct(priceCents, costCents) {
    if (priceCents <= 0)
        return null;
    return (marginCents(priceCents, costCents) / priceCents) * 100;
}
/** Sobreprecio sobre el costo, en porcentaje. Null si no hay costo registrado. */
export function markupPct(priceCents, costCents) {
    if (costCents <= 0)
        return null;
    return (marginCents(priceCents, costCents) / costCents) * 100;
}
/**
 * Precio a partir del costo mas una ganancia FIJA en pesos.
 *
 *   precio = costo + ganancia
 *
 * Es la forma en que de verdad piensa quien atiende una tienda: "a este le gano
 * veinte pesos el kilo". No tiene ninguna ambiguedad, y por eso es el modo por
 * omision de la calculadora.
 */
export function priceFromProfitCents(costCents, profitCents) {
    return assertSafeInt(costCents, 'costo') + assertSafeInt(profitCents, 'ganancia');
}
/**
 * Precio a partir del costo mas un porcentaje SOBRE EL COSTO (sobreprecio).
 *
 *   precio = costo * (1 + pct/100)
 *
 * OJO CON LA CONFUSION MAS CARA DEL COMERCIO
 * ------------------------------------------
 * "Ganar 30 %" significa dos cosas distintas y la diferencia es dinero real:
 *
 *   sobre el COSTO  (esta funcion):  costo 100 -> precio 130
 *   sobre el PRECIO (`marginPct`):   costo 100 -> precio 142.86
 *
 * Quien atiende una tienda casi siempre quiere decir lo primero ("le subo un
 * 30 %"), mientras que los reportes y los margenes del sistema usan lo segundo.
 * Por eso la interfaz nunca dice "30 %" a secas: dice "30 % sobre el costo" o
 * "% del precio", y muestra tambien la ganancia en pesos, que no se presta a
 * interpretaciones.
 */
export function priceFromMarkupPct(costCents, pct) {
    assertSafeInt(costCents, 'costo');
    if (!Number.isFinite(pct) || pct < 0) {
        throw new MoneyError(`sobreprecio invalido: ${pct}`);
    }
    // Se escala a diezmilesimas para admitir porcentajes con dos decimales sin
    // arrastrar flotantes hasta el precio final.
    const scaled = 10_000 + Math.round(pct * 100);
    return divRoundHalfUp(costCents * scaled, 10_000);
}
/**
 * Aplica un descuento porcentual con un solo redondeo.
 * @param pct porcentaje entre 0 y 100 (admite decimales)
 */
export function discountCents(amountCents, pct) {
    assertSafeInt(amountCents, 'importe');
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
        throw new MoneyError(`porcentaje de descuento invalido: ${pct}`);
    }
    // Se escala a diezmilesimas para admitir porcentajes con dos decimales sin
    // arrastrar flotantes hasta el resultado final.
    const scaledPct = Math.round(pct * 100);
    return divRoundHalfUp(amountCents * scaledPct, 10_000);
}
/**
 * Redondeo de efectivo al multiplo mas cercano.
 *
 * Muchas tiendas mexicanas redondean el total en efectivo a 50 centavos porque
 * las monedas de 10 y 20 centavos practicamente no circulan. Es OPCIONAL y por
 * sucursal (`stores.cash_rounding_cents`); con paso 0 no se aplica nada.
 */
export function roundCashCents(cents, stepCents) {
    assertSafeInt(cents, 'importe');
    assertSafeInt(stepCents, 'paso de redondeo');
    if (stepCents <= 1)
        return cents;
    return divRoundHalfUp(cents, stepCents) * stepCents;
}
/** Cambio a devolver. Negativo significa que el pago no alcanza. */
export function changeCents(receivedCents, totalCents) {
    return assertSafeInt(receivedCents, 'recibido') - assertSafeInt(totalCents, 'total');
}
// ---------------------------------------------------------------------------
// Formato y parseo. Exclusivamente para el borde de la interfaz.
// ---------------------------------------------------------------------------
const MXN = new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});
const PLAIN = new Intl.NumberFormat('es-MX', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});
/** "$15.50" — para mostrar, jamas para calcular. */
export function formatMoney(cents) {
    return MXN.format(cents / 100);
}
/** "15.50" — sin simbolo, util dentro de tablas y campos de captura. */
export function formatMoneyPlain(cents) {
    return PLAIN.format(cents / 100);
}
/** "$1,234" — sin centavos, para tarjetas de resumen donde el detalle estorba. */
export function formatMoneyShort(cents) {
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(cents / 100);
}
/**
 * Convierte lo que el usuario escribio a centavos.
 * Acepta "15.50", "15,50", "$15.50", "1,234.50", " 15 ".
 * Devuelve null si no es interpretable; el llamador decide como avisar.
 */
export function parseMoneyToCents(input) {
    const raw = input.trim().replace(/[$\s]/g, '');
    if (raw === '')
        return null;
    let normalized;
    if (raw.includes('.') && raw.includes(',')) {
        // "1,234.50" -> la coma es separador de miles
        normalized = raw.replace(/,/g, '');
    }
    else if (raw.includes(',')) {
        // Coma sola: separador de miles si le siguen exactamente 3 digitos, si no es decimal.
        normalized = /,\d{3}$/.test(raw) ? raw.replace(/,/g, '') : raw.replace(',', '.');
    }
    else {
        normalized = raw;
    }
    if (!/^-?\d*\.?\d*$/.test(normalized) || normalized === '.' || normalized === '-') {
        return null;
    }
    const value = Number(normalized);
    if (!Number.isFinite(value))
        return null;
    return divRoundHalfUp(Math.round(value * 1000), 10);
}
/** Cantidad en milesimas a numero decimal. Solo para mostrar. */
export function milliToNumber(qtyMilli) {
    return qtyMilli / MILLI;
}
/** Numero decimal a milesimas, redondeando. Usar al capturar un peso. */
export function numberToMilli(qty) {
    if (!Number.isFinite(qty))
        throw new MoneyError(`cantidad invalida: ${qty}`);
    return Math.round(qty * MILLI);
}
/**
 * "3" para piezas enteras, "0.750" para granel.
 * Las piezas se muestran sin decimales porque "3.000 pz" se lee como un error.
 */
export function formatQty(qtyMilli, isWeighed) {
    if (!isWeighed && qtyMilli % MILLI === 0) {
        return String(qtyMilli / MILLI);
    }
    return (qtyMilli / MILLI).toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
}
/*
 * `formatQtyWithUnit` vive en `units.ts`, no aqui.
 *
 * Este modulo no importa NADA a proposito —es la capa de abajo, la que sabe de
 * enteros y de redondeo— y pegarle una unidad a una cantidad ya exige conocer el
 * catalogo de unidades y su abreviatura. Escrita aqui, la unica salida era pegar
 * el codigo crudo del enum, que es como la interfaz acabo diciendo "200 b50".
 */
/** Convierte lo que el usuario escribio a milesimas. Null si no es interpretable. */
export function parseQtyToMilli(input) {
    const raw = input.trim().replace(/[\s]/g, '').replace(',', '.');
    if (raw === '')
        return null;
    if (!/^\d*\.?\d*$/.test(raw) || raw === '.')
        return null;
    const value = Number(raw);
    if (!Number.isFinite(value) || value < 0)
        return null;
    return Math.round(value * MILLI);
}
//# sourceMappingURL=money.js.map